import type { RowChange, SessionDelta, SessionRow } from './types'

/** Дельта без правок. */
export const EMPTY_DELTA: SessionDelta = { added: [], changes: {}, removed: [] }

export const hasChanges = (delta: SessionDelta): boolean =>
  delta.added.length > 0 || delta.removed.length > 0 || Object.keys(delta.changes).length > 0

function applyChange(row: SessionRow, change: RowChange | undefined): SessionRow {
  if (change === undefined) return row
  return {
    ...row,
    ...(change.counted !== undefined ? { counted: change.counted } : {}),
    ...(change.reprint !== undefined ? { reprint: change.reprint } : {}),
  }
}

/**
 * Строки, которые видит человек и которые уйдут сохранением: новые сверху
 * (так сканируют полку), затем сохранённые в порядке сервера; удалённые
 * убраны, правки рабочих полей наложены. Та же функция — для экрана и для
 * тела PUT: «показали» и «сохранили» не расходятся.
 */
export function mergeRows(server: readonly SessionRow[], delta: SessionDelta): SessionRow[] {
  const removed = new Set(delta.removed)
  const added = delta.added.filter((row) => !removed.has(row.id))
  const known = new Set(added.map((row) => row.id))
  const kept = server.filter((row) => !removed.has(row.id) && !known.has(row.id))
  return [...added, ...kept].map((row) => applyChange(row, delta.changes[row.id]))
}

/**
 * Что из дельты подтверждено ответом сервера: отправленные новые строки,
 * удаления и правки, значение которых с тех пор не менялось. Правка,
 * сделанная во время сохранения, остаётся в дельте и уйдёт следующим.
 */
export function acknowledge(current: SessionDelta, sent: SessionDelta): SessionDelta {
  const sentAdded = new Set(sent.added.map((row) => row.id))
  const sentRemoved = new Set(sent.removed)
  const changes: Record<string, RowChange> = {}
  for (const [id, change] of Object.entries(current.changes)) {
    const was = sent.changes[id]
    const left: RowChange = {}
    if (change.counted !== undefined && (was === undefined || was.counted !== change.counted)) left.counted = change.counted
    if (change.reprint !== undefined && (was === undefined || was.reprint !== change.reprint)) left.reprint = change.reprint
    if (Object.keys(left).length > 0) changes[id] = left
  }
  return {
    added: current.added.filter((row) => !sentAdded.has(row.id)),
    changes,
    removed: current.removed.filter((id) => !sentRemoved.has(id)),
  }
}
